import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Lock, ArrowLeft, BookOpen, Music, Headphones, Video, ShieldAlert, LogIn, Flame, CheckCircle2, Sunrise, Sun, Sunset, Moon, Clock, Timer } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import ShareButton from '../components/ShareButton';
import { MediaBadges, CategoryBadge } from '../components/MediaBadges';
import api, { errText, mediaUrl } from '../lib/api';
import { isDirectAudio, isDirectVideo, toResourceEmbed } from '../lib/embed';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

const scEmbed = (url) => {
  if (!url) return null;
  if (url.includes('w.soundcloud.com/player')) return url;  // already an embed URL
  if (/soundcloud\.com/.test(url)) return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%234A1721&auto_play=false`;
  return null;
};

const durationLabel = (m) => {
  m = Math.round(m || 0);
  const h = Math.floor(m / 60), mins = m % 60, parts = [];
  if (h) parts.push(`${h} hour${h > 1 ? 's' : ''}`);
  if (mins) parts.push(`${mins} minute${mins > 1 ? 's' : ''}`);
  return parts.join(' ') || '0 minutes';
};
const TIME_ICON = { dawn: Sunrise, morning: Sunrise, afternoon: Sun, evening: Sunset, night: Moon, anytime: Clock };

const parseStages = (text) => {
  if (!text) return null;
  const parts = text.split(/(?=Stage\s+\d+\s*[—–-])/g).map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  return parts.map((p, i) => {
    const m = p.match(/^Stage\s+(\d+)\s*[—–-]\s*([^:]+):\s*([\s\S]*)$/);
    if (!m) return { num: String(i + 1), title: '', duration: '', body: p };
    let head = m[2].trim();
    const body = m[3].trim();
    let title = head, duration = '';
    const cm = head.match(/^(.*?),\s*([^,]*(?:minutes?|mins?|seconds?|secs?|hours?|hrs?)[^,]*)$/i);
    if (cm) { title = cm[1].trim(); duration = cm[2].trim(); }
    return { num: m[1], title, duration, body };
  });
};

const StagesSection = ({ text }) => {
  const stages = parseStages(text);
  return (
    <section data-testid="stages-section">
      <h2 className="font-serif-display text-2xl text-burgundy mb-4">Stages</h2>
      {stages ? (
        <ol className="space-y-3">
          {stages.map((s, i) => (
            <li key={i} className="flex gap-4 rounded-2xl bg-white border border-rose/30 p-4" data-testid={`stage-${s.num}`}>
              <div className="shrink-0 w-9 h-9 rounded-full bg-burgundy text-cream flex items-center justify-center font-serif-display text-lg">{s.num}</div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {s.title && <h3 className="font-serif-display text-lg text-burgundy leading-tight">{s.title}</h3>}
                  {s.duration && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-soft/60 text-mahogany"><Timer className="w-3 h-3" /> {s.duration}</span>}
                </div>
                {s.body && <p className="mt-1.5 text-sm text-mahogany/85 leading-relaxed">{s.body}</p>}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <div className="prose prose-sm max-w-none text-mahogany/90 whitespace-pre-line leading-relaxed">{text}</div>
      )}
    </section>
  );
};

const DisclaimerBox = ({ text, agreed, onToggle }) => (
  <div className="mt-5 text-left rounded-2xl bg-rose-soft/30 border border-rose/40 p-5" data-testid="disclaimer-box">
    <div className="flex items-center gap-2 text-burgundy font-medium mb-2"><ShieldAlert className="w-4 h-4" /> Health &amp; safety disclaimer</div>
    <p className="text-xs text-mahogany/75 leading-relaxed max-h-40 overflow-y-auto pr-1">{text}</p>
    <label className="mt-3 flex items-start gap-2 text-sm text-mahogany cursor-pointer">
      <input type="checkbox" checked={agreed} onChange={(e) => onToggle(e.target.checked)} className="mt-0.5" data-testid="disclaimer-agree" />
      <span>I have read, understood and agree. I practise at my own risk and will follow my health professional's advice.</span>
    </label>
  </div>
);

const MeditationDetail = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const { user, checkAuth } = useAuth();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/meditations/${slug}`).then((r) => setT(r.data)).catch(() => setT(null)).finally(() => setLoading(false));
  useEffect(() => { setAgreed(false); load(); /* eslint-disable-next-line */ }, [slug]);

  const unlockFree = async () => {
    if (!agreed) return toast({ title: 'Please agree to the disclaimer first' });
    setBusy(true);
    try { await api.post(`/meditations/${slug}/unlock-free`, { signature: user?.full_name }); toast({ title: 'Unlocked — enjoy 🧘' }); setLoading(true); await load(); checkAuth(); }
    catch (e) { toast({ title: 'Could not unlock', description: errText(e) }); }
    finally { setBusy(false); }
  };

  const buyUnlock = async () => {
    if (!agreed) return toast({ title: 'Please agree to the disclaimer first' });
    setBusy(true);
    try {
      const r = await api.post(`/meditations/${slug}/checkout`, { origin_url: window.location.origin, disclaimer_agreed: true, signature: user?.full_name });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast({ title: 'Checkout failed', description: errText(e) }); setBusy(false); }
  };

  const acknowledge = async () => {
    if (!agreed) return toast({ title: 'Please agree to the disclaimer first' });
    setBusy(true);
    try { await api.post(`/meditations/${slug}/acknowledge`, { signature: user?.full_name }); setLoading(true); await load(); }
    catch (e) { toast({ title: 'Could not continue', description: errText(e) }); }
    finally { setBusy(false); }
  };

  const [practicing, setPracticing] = useState(false);
  const logPractice = async () => {
    setPracticing(true);
    try {
      const r = await api.post(`/meditations/${slug}/practice`);
      setT((prev) => ({ ...prev, progress: r.data }));
      toast({ title: 'Logged 🧘 Well done!' });
    } catch (e) { toast({ title: 'Could not log', description: errText(e) }); }
    finally { setPracticing(false); }
  };

  if (loading) return <div className="min-h-screen bg-cream"><Header /><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div><Footer /></div>;
  if (!t) return <div className="min-h-screen bg-cream"><Header /><div className="min-h-[60vh] flex items-center justify-center text-mahogany/60">Technique not found.</div><Footer /></div>;

  const embed = toResourceEmbed(t.video_url) || ytEmbed(t.video_url);
  // Uploaded files are served at /api/uploads/<id> (no extension) — play them inline too.
  const isUploadedFile = (u) => /\/uploads\//.test(u || '');
  const playableVideo = t.video_url && !embed && (isDirectVideo(t.video_url) || isUploadedFile(t.video_url));
  const price = t.effective_price;

  return (
    <div className="min-h-screen bg-cream" data-testid="meditation-detail">
      <Header />
      <div className="max-w-4xl mx-auto px-5 py-12">
        <Link to="/meditations" className="inline-flex items-center gap-2 text-sm text-mahogany/70 hover:text-burgundy mb-6"><ArrowLeft className="w-4 h-4" /> All techniques</Link>

        <div className="relative rounded-3xl overflow-hidden border border-rose/30 aspect-[16/10] sm:aspect-[21/9]" data-testid="detail-hero">
          {t.hero_image
            ? <SmartImage src={t.hero_image} alt={t.title} className="absolute inset-0 w-full h-full" />
            : <div className="absolute inset-0 bg-rose-soft/40" />}
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(58,18,27,0.88) 0%, rgba(58,18,27,0.35) 45%, rgba(58,18,27,0.05) 100%)' }} />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-9">
            <div className="text-xs uppercase tracking-[0.22em] text-cream/85 mb-2">{t.purpose}</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-[1.08]" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.55)' }}>{t.title}</h1>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="detail-quickfacts">
          {t.time_of_day && (() => { const Ico = TIME_ICON[t.time_of_day] || Clock; return (
            <span className="inline-flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full bg-burgundy text-cream capitalize" data-testid="detail-time-of-day"><Ico className="w-4 h-4" /> {t.time_of_day}</span>
          ); })()}
          {t.recommended_time && <span className="inline-flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full bg-white border border-rose/40 text-mahogany" data-testid="detail-recommended-time"><Clock className="w-4 h-4 text-rose-italic" /> {t.recommended_time}</span>}
          {(t.durations || []).length > 0 && <span className="inline-flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full bg-white border border-rose/40 text-mahogany"><Timer className="w-4 h-4 text-rose-italic" /> {durationLabel(Math.min(...t.durations))}{t.durations.length > 1 ? '+' : ''}</span>}
        </div>

        <p className="mt-5 text-mahogany/80 max-w-2xl">{t.summary}</p>
        <div className="mt-4">
          <ShareButton url={`${window.location.origin}/meditations/${t.slug}`} title={`${t.title} — Osho Universal`} text={t.summary || `Explore ${t.title} on Osho Universal`} label="Share this meditation" testid="meditation-share" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="detail-categories">
          <CategoryBadge tech={t} />
          <MediaBadges tech={t} />
        </div>

        {!t.entitled ? (
          <div className="mt-8 p-8 rounded-3xl bg-white border border-rose/40 text-center" data-testid="locked-panel">
            <Lock className="w-8 h-8 mx-auto text-mahogany/50" />
            <h3 className="mt-3 font-serif-display text-2xl text-burgundy">This technique is for members</h3>
            <p className="mt-2 text-mahogany/70 max-w-md mx-auto">{t.instructions_preview}…</p>

            {!user ? (
              <Link to="/login" className="pill-btn mt-5 inline-flex" data-testid="signin-to-unlock"><LogIn className="w-4 h-4" /> Sign in to unlock</Link>
            ) : (
              <>
                <DisclaimerBox text={t.disclaimer} agreed={agreed} onToggle={setAgreed} />
                <div className="mt-5 flex flex-col items-center gap-3">
                  {t.can_unlock_free && (
                    <button onClick={unlockFree} disabled={busy || !agreed} className="pill-btn disabled:opacity-50" data-testid="unlock-free-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Unlock free (uses 1 of your free picks)</button>
                  )}
                  {price > 0 && (
                    <button onClick={buyUnlock} disabled={busy || !agreed} className="pill-btn disabled:opacity-50" data-testid="unlock-paid-btn">
                      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                      Unlock for {t.discount_price != null && <span className="line-through opacity-60 mx-1">${t.price}</span>}${price} one-time
                    </button>
                  )}
                  <Link to="/membership" className="text-sm text-mahogany/70 underline hover:text-burgundy" data-testid="upgrade-link">or upgrade to Meditators for all techniques</Link>
                </div>
              </>
            )}
          </div>
        ) : !t.acknowledged ? (
          <div className="mt-8 p-8 rounded-3xl bg-white border border-rose/40 text-center" data-testid="acknowledge-panel">
            <ShieldAlert className="w-8 h-8 mx-auto text-mahogany/50" />
            <h3 className="mt-3 font-serif-display text-2xl text-burgundy">Before you begin</h3>
            <p className="mt-2 text-mahogany/70 max-w-md mx-auto">Please review and accept the disclaimer to reveal the practice.</p>
            <DisclaimerBox text={t.disclaimer} agreed={agreed} onToggle={setAgreed} />
            <button onClick={acknowledge} disabled={busy || !agreed} className="pill-btn mt-5 disabled:opacity-50" data-testid="acknowledge-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} I understand — show me the practice</button>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white border border-rose/40 p-4" data-testid="practice-bar">
              <div className="flex items-center gap-4 text-sm text-mahogany/80">
                <span className="inline-flex items-center gap-1.5 font-medium text-burgundy"><Flame className="w-4 h-4 text-rose-italic" /> {t.progress?.streak || 0}-day streak</span>
                <span data-testid="practice-count">Practised {t.progress?.technique_count || 0}×</span>
              </div>
              <button onClick={logPractice} disabled={practicing} className="pill-btn disabled:opacity-50" data-testid="log-practice-btn">{practicing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} I practised this</button>
            </div>
            <div className="text-xs text-mahogany/60 flex items-center gap-1.5 rounded-xl bg-rose-soft/20 border border-rose/30 px-3 py-2"><ShieldAlert className="w-3.5 h-3.5" /> Practise at your own risk and follow your health professional's advice.</div>

            {t.overview && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3">Overview</h2>
                <div className="prose prose-sm max-w-none text-mahogany/90 whitespace-pre-line leading-relaxed">{t.overview}</div></section>
            )}
            {t.key_principle && (
              <div className="rounded-2xl bg-rose-soft/25 border border-rose/30 p-4" data-testid="key-principle">
                <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1">Key principle</div>
                <p className="font-serif-display text-lg text-burgundy leading-snug">{t.key_principle}</p>
              </div>
            )}
            {(t.activity_level || t.setting || t.space_notes) && (
              <section data-testid="technique-meta">
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">At a glance</h2>
                <dl className="grid sm:grid-cols-2 gap-3">
                  {t.activity_level && <div className="rounded-xl bg-white border border-rose/30 px-3 py-2"><dt className="text-[11px] uppercase tracking-widest text-mahogany/55">Activity level</dt><dd className="text-sm text-mahogany/90">{t.activity_level}</dd></div>}
                  {t.setting && <div className="rounded-xl bg-white border border-rose/30 px-3 py-2"><dt className="text-[11px] uppercase tracking-widest text-mahogany/55">Setting</dt><dd className="text-sm text-mahogany/90">{t.setting}</dd></div>}
                  {t.space_notes && <div className="rounded-xl bg-white border border-rose/30 px-3 py-2"><dt className="text-[11px] uppercase tracking-widest text-mahogany/55">Space notes</dt><dd className="text-sm text-mahogany/90">{t.space_notes}</dd></div>}
                </dl>
              </section>
            )}
            {t.preparation && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3">Preparation</h2>
                <div className="prose prose-sm max-w-none text-mahogany/90 whitespace-pre-line leading-relaxed">{t.preparation}</div></section>
            )}
            {t.stages && <StagesSection text={t.stages} />}
            {t.instructions && (
              <section>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">How to practise</h2>
                <div className="prose prose-sm max-w-none text-mahogany/90 whitespace-pre-line leading-relaxed">{t.instructions}</div>
              </section>
            )}
            {t.after_meditation && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3">After meditation</h2>
                <div className="prose prose-sm max-w-none text-mahogany/90 whitespace-pre-line leading-relaxed">{t.after_meditation}</div></section>
            )}
            {embed && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3 flex items-center gap-2"><Video className="w-5 h-5" /> Video guidance</h2>
                <div className="aspect-video rounded-2xl overflow-hidden border border-rose/30"><iframe title="video" src={embed} className="w-full h-full" allowFullScreen /></div></section>
            )}
            {playableVideo && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3 flex items-center gap-2"><Video className="w-5 h-5" /> Video guidance</h2>
                <div className="rounded-2xl overflow-hidden border border-rose/30 bg-black">
                  <video controls preload="metadata" playsInline poster={(t.video_poster || t.hero_image) ? mediaUrl(t.video_poster || t.hero_image) : undefined}
                    controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
                    src={mediaUrl(t.video_url)} className="w-full aspect-video" data-testid="med-video-player" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-mahogany/70" data-testid="med-video-share">
                  <ShareButton title={t.title} text={t.title} compact label="Share" testid="share-med-video" />
                  <span>Want to download this video or feature it on your website? <Link to="/contacts" className="underline text-burgundy hover:text-mahogany">Contact us</Link>.</span>
                </div></section>
            )}
            {t.video_url && !embed && !playableVideo && <a href={t.video_url} target="_blank" rel="noreferrer" className="pill-btn-outline inline-flex"><Video className="w-4 h-4" /> Watch video</a>}
            {t.audio_url && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3 flex items-center gap-2"><Headphones className="w-5 h-5" /> Guided audio</h2>
                <audio controls src={mediaUrl(t.audio_url)} className="w-full" data-testid="audio-player" /></section>
            )}
            {t.music_url && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3 flex items-center gap-2"><Music className="w-5 h-5" /> Music</h2>
                {ytEmbed(t.music_url)
                  ? <div className="aspect-video rounded-2xl overflow-hidden border border-rose/30"><iframe title="music" src={ytEmbed(t.music_url)} className="w-full h-full" allowFullScreen /></div>
                  : isDirectAudio(t.music_url)
                    ? <audio controls preload="none" src={t.music_url} className="w-full" data-testid="music-audio-player" />
                    : scEmbed(t.music_url)
                      ? <iframe title="music" src={scEmbed(t.music_url)} className="w-full rounded-2xl border border-rose/30" style={{ height: '400px' }} allow="autoplay" data-testid="soundcloud-embed" />
                      : <a href={t.music_url} target="_blank" rel="noreferrer" className="pill-btn-outline inline-flex"><Music className="w-4 h-4" /> Open music</a>}
                {t.music_url && (
                  <p className="mt-2 text-xs text-mahogany/55" data-testid="music-source-note">
                    Audio source: <a href={t.music_url} target="_blank" rel="noreferrer" className="underline">{(() => { try { return new URL(t.music_url).hostname.replace('www.', ''); } catch { return 'external source'; } })()}</a>. Osho Universal does not own or claim rights to this recording — see our <Link to="/copyright-policy" className="underline">Copyright &amp; DMCA Policy</Link>.
                  </p>
                )}</section>
            )}
            {t.source && (
              <p className="text-sm text-mahogany/60" data-testid="technique-source"><span className="uppercase tracking-widest text-[11px] text-mahogany/50">Source</span> · {t.source}</p>
            )}
            {(t.references || []).length > 0 && (
              <section><h2 className="font-serif-display text-2xl text-burgundy mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5" /> References &amp; teachings</h2>
                <ul className="space-y-3">
                  {t.references.map((r, i) => (
                    <li key={i} className="p-4 rounded-2xl bg-white border border-rose/30">
                      <div className="font-medium text-burgundy">{r.link ? <a href={r.link} target="_blank" rel="noreferrer" className="underline">{r.title}</a> : r.title}{r.author ? ` — ${r.author}` : ''}</div>
                      {r.note && <p className="text-sm text-mahogany/70 mt-1">{r.note}</p>}
                    </li>
                  ))}
                </ul></section>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MeditationDetail;
